import type { ServerToClientMessage } from '../types';
import { useStrategyStore } from '../../stores/strategyStore';
import type { StrategyInfo, StrategySignalData } from '../../stores/strategyStore';
import { usePortfoliosStore } from '../../stores/portfoliosStore';
import { useRebalancesStore } from '../../stores/rebalancesStore';
import { useExecutionsStore } from '../../stores/executionsStore';
import { useRegimeStore } from '../../stores/regimeStore';

const strategyTracker = new Map<string, { warm: boolean; signals: number; lastTs: number }>();

/** Strategy name cache — populated at runtime from incoming signals. */
const STRATEGY_NAMES: Record<string, string> = {};

function updateStrategyFromSignal(s: StrategySignalData): void {
  const existing = strategyTracker.get(s.strategyId);
  if (existing) {
    existing.signals++;
    if (s.warmupComplete) existing.warm = true;
    existing.lastTs = Math.max(existing.lastTs, s.evaluatedAt);
  } else {
    strategyTracker.set(s.strategyId, {
      warm: s.warmupComplete,
      signals: 1,
      lastTs: s.evaluatedAt,
    });
  }

  const store = useStrategyStore.getState();
  const trades = store.trades;
  const strategies: StrategyInfo[] = [];

  for (const [id, info] of strategyTracker) {
    const stratTrades = trades.filter((t) => t.strategyId === id);
    const closed = stratTrades.filter((t) => t.action === 'close');
    const wins = closed.filter((t) => (t.realizedPnl ?? 0) > 0).length;
    const losses = closed.filter(
      (t) => (t.realizedPnl ?? 0) <= 0 && t.realizedPnl != null,
    ).length;
    const totalPnl = closed.reduce((sum, t) => sum + (t.realizedPnl ?? 0), 0);
    const open = stratTrades.filter((t) => t.action === 'open').length;

    strategies.push({
      id,
      name: STRATEGY_NAMES[id] ?? id,
      description: '',
      enabled: true,
      warmupComplete: info.warm,
      signalCount: info.signals,
      tradeCount: stratTrades.length,
      winCount: wins,
      lossCount: losses,
      totalPnl,
      openPositions: open,
      allocatedBalance: 0,
      returnPct: 0,
    });
  }

  const portfolio = store.portfolio;
  if (portfolio && strategies.length > 0) {
    const perStrategy = portfolio.totalEquity / strategies.length;
    for (const strategy of strategies) strategy.allocatedBalance = perStrategy;
  }

  store.setStrategies(strategies);
}

export function dispatchStrategyMessage(msg: ServerToClientMessage): boolean {
  switch (msg.type) {
    case 'strategy-signal':
      useStrategyStore.getState().addSignal(msg.data);
      updateStrategyFromSignal(msg.data);
      return true;
    case 'strategy-signal-snapshot':
      useStrategyStore.getState().setSignals(msg.signals);
      if (Array.isArray(msg.signals)) {
        for (const signal of msg.signals) updateStrategyFromSignal(signal);
      }
      return true;
    case 'paper-trade':
      useStrategyStore.getState().addTrade(msg.data);
      return true;
    case 'paper-trade-snapshot':
      useStrategyStore.getState().setTrades(msg.trades);
      return true;
    case 'portfolio-update':
    case 'portfolio-snapshot':
      // Feed both the legacy single-portfolio strategyStore (for gradual
      // migration) and the new per-portfolio store which is the source of
      // truth for the Wallet/Portfolios UI.
      useStrategyStore.getState().setPortfolio(msg.data);
      usePortfoliosStore.getState().applySnapshot(msg.data);
      return true;
    case 'rebalance':
      useRebalancesStore.getState().add(msg.data);
      return true;
    case 'rebalance-snapshot':
      useRebalancesStore.getState().setSnapshot(msg.rebalances);
      return true;
    case 'execution':
      useExecutionsStore.getState().add(msg.data);
      return true;
    case 'execution-snapshot':
      useExecutionsStore.getState().setSnapshot(msg.executions);
      return true;
    case 'regime':
      useRegimeStore.getState().set(msg.data);
      return true;
    case 'regime-snapshot':
      useRegimeStore.getState().setSnapshot(msg.regimes);
      return true;
    default:
      return false;
  }
}
